// src/components/auth/Login.jsx
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { changeLanguage } from '../../i18n/i18n';

const LANGS = [
    { code: 'it', flag: '🇮🇹', label: 'IT' },
    { code: 'fr', flag: '🇫🇷', label: 'FR' },
    { code: 'en', flag: '🇬🇧', label: 'EN' },
];

const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

const Logo = ({ size = 48 }) => (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M50 5 L90 25 L90 65 Q90 85 50 97 Q10 85 10 65 L10 25 Z" fill="#1d4ed8" />
        <path d="M50 12 L84 30 L84 64 Q84 80 50 91 Q16 80 16 64 L16 30 Z" fill="#2563eb" />
        <text x="28" y="58" fontFamily="Arial" fontWeight="bold" fontSize="32" fill="white" fontStyle="italic">i</text>
        <text x="42" y="62" fontFamily="Arial" fontWeight="bold" fontSize="36" fill="white">P</text>
    </svg>
);

const Login = () => {
    const { t, i18n } = useTranslation();
    const [form, setForm] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [attempts, setAttempts] = useState(0);

    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Messaggio da Register o da redirect
    const successMessage = location.state?.message;
    const from = location.state?.from?.pathname || '/dashboard';

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: name === 'email' ? value.trimStart() : value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const e = {};
        const email = form.email.trim();
        if (!email) e.email = 'Email richiesta';
        else if (!validateEmail(email)) e.email = 'Formato email non valido';
        if (!form.password) e.password = 'Password richiesta';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        if (attempts >= 5) {
            setErrors({ general: 'Troppi tentativi. Aspetta qualche minuto.' });
            return;
        }
        setLoading(true);
        setErrors({});
        const result = await login(form.email.trim().toLowerCase(), form.password);
        setLoading(false);
        if (result.success) {
            setAttempts(0);
            navigate(from, { replace: true });
        } else {
            setAttempts(prev => prev + 1);
            setErrors({ general: result.error || 'Credenziali non valide' });
        }
    };

    const ic = (err) => `w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-sm ${err ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`;

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-indigo-600/10 rounded-full blur-3xl" />
            </div>

            <div className="relative max-w-md w-full mx-4">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <Logo size={56} />
                        <div className="text-left">
                            <h1 className="text-3xl font-bold text-white tracking-tight">InfraPilot</h1>
                            <p className="text-blue-300 text-sm">Construction Site Management</p>
                        </div>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-xs text-slate-400 mb-2">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full inline-block" />
                        Piattaforma sicura — dati crittografati
                    </div>
                    {/* Bottoni lingua */}
                    <div className="flex items-center justify-center gap-1 mt-2">
                        {LANGS.map(l => (
                            <button key={l.code} onClick={() => changeLanguage(l.code)}
                                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition ${
                                    i18n.language === l.code
                                        ? 'bg-blue-600 text-white'
                                        : 'text-slate-400 hover:text-white hover:bg-white/10'
                                }`}>
                                {l.flag} {l.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-8">
                    <h2 className="text-xl font-bold text-gray-900 mb-1">Accedi al tuo account</h2>
                    <p className="text-gray-500 text-sm mb-6">Inserisci le tue credenziali per continuare</p>

                    {/* Messaggio da Register */}
                    {successMessage && (
                        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2">
                            ✅ {successMessage}
                        </div>
                    )}

                    {errors.general && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                            ⚠️ {errors.general}
                        </div>
                    )}

                    {attempts >= 3 && attempts < 5 && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-700 text-sm">
                            ⚠️ {5 - attempts} tentativi rimasti prima del blocco temporaneo
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                            <input type="email" name="email" autoComplete="email"
                                value={form.email} onChange={handleChange}
                                className={ic(errors.email)} placeholder="nome@azienda.com" />
                            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                            <div className="relative">
                                <input type={showPassword ? 'text' : 'password'} name="password"
                                    autoComplete="current-password" value={form.password}
                                    onChange={handleChange} className={`${ic(errors.password)} pr-10`}
                                    placeholder="••••••••" />
                                <button type="button" tabIndex={-1}
                                    onClick={() => setShowPassword(p => !p)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                                </button>
                            </div>
                            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                        </div>

                        <div className="flex justify-end">
                            <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline">
                                {t('auth.forgotPassword')}
                            </Link>
                        </div>

                        <button type="submit" disabled={loading || attempts >= 5}
                            className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm">
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Accesso in corso...
                                </span>
                            ) : 'Accedi'}
                        </button>
                    </form>

                    <p className="mt-6 text-center text-sm text-gray-500">
                        {t('auth.noAccount')}{' '}
                        <Link to="/register" className="text-blue-600 hover:underline font-semibold">
                            {t('auth.register')} gratuitamente
                        </Link>
                    </p>
                </div>

                <p className="text-center text-xs text-slate-500 mt-6">
                    © {new Date().getFullYear()} InfraPilot · Tutti i diritti riservati
                </p>
            </div>
        </div>
    );
};

export default Login;